<template>
    <div>
        <v-popper class="no-flex" position="bottomEnd" ref="csvUpload" :offset="8">
            <div slot="trigger">
                <v-icon-button class="no-padding" size="small" type="basic" v-popper-trigger>
                    <v-icon icon="actions"/>
                </v-icon-button>
            </div>
            <div class="popover" slot="content">
                <div class="popover-control">
                    <div class="popover-content popover-menu">
                        <a class="popover-menu-item" @click="toggleModal('Update')">Edit Product</a>
                        <a class="popover-menu-item popover-menu-item-negative" @click="toggleModal('Delete')">Delete
                            Product</a>
                    </div>
                </div>
            </div>
        </v-popper>
        <!-- Edit Product Modal -->
        <v-modal v-if="showProductUpdateModal" title="Edit Product" @close="toggleModal('Update')" size="small">
            <div slot="content">
                <v-error-messages :has-frontend-errors="$v.product.$error" class="stack-l">
                    <li class="list-item" v-if="!$v.product.name.required">Product name is required</li>
                    <li class="list-item" v-if="!$v.product.name.minLength">Product name must be 3 characters or
                        longer
                    </li>
                </v-error-messages>
                <p class="stack-m">Edit your product information.</p>
                <div class="grid-content">
                    <div class="grid-item grid-item-full">
                        <r-input label="Product Name" v-model="product.name" :validate="$v.product.name"/>
                    </div>
                    <div class="grid-item grid-item-full">
                        <r-input multiline rows="3" :max-length="512" label="Description" v-model="product.description" :validate="$v.product.description"/>
                    </div>
                    <div class="grid-item grid-item-full">
                        <r-input label="Unit Name" v-model="product.unitLabel"/>
                    </div>
                    <div class="grid-item grid-item-full">
                        <r-input label="Accounting Code" v-model="product.accountingCode"/>
                    </div>
                    <div class="grid-item grid-item-half grid-item-break-half">
                        <r-checkbox
                            label="Is Taxable"
                            :value="true"
                            v-model="isTaxable"
                        />
                    </div>
                    <div v-if="isTaxable" class="grid-item grid-item-full">
                        <v-select
                            label="Tax Category ID"
                            :options="taxCategoriesOptions"
                            :custom-label="nameWithTaxCode"
                            option-label="name"
                            option-key="taxCode"
                            placeholder="Select Tax Category ID"
                            v-model="product.taxCategoryId"
                        />
                    </div>
                    <div class="grid-item grid-item-half grid-item-break-half">
                        <r-checkbox
                            label="Is Shippable"
                            :value="true"
                            v-model="product.requiresShipping"
                        />
                    </div>
                    <v-custom-fields-manager
                        fluid
                        resource-type="products"
                        :preload-custom-fields="productsCustomFieldsSchema"
                        v-model="product.customFields"/>
                </div>
            </div>
            <template slot="actions">
                <r-button type="primary" @click="updateProduct">Update Product</r-button>
            </template>
        </v-modal>
        <!-- Delete Product Modal -->
        <v-modal v-if="showProductDeleteModal" title="Delete Product" @close="toggleModal('Delete')"
                 size="small">
            <div slot="content">
                <p v-if="hasPlans">
                    This product cannot be deleted because it contains plans. Only products without plans can
                    be deleted.
                </p>
                <p v-else>Are you sure you want to delete this product?</p>
            </div>
            <template slot="actions" v-if="!hasPlans">
                <r-button type="danger" @click="deleteProduct">Delete Product</r-button>
            </template>
        </v-modal>
    </div>
</template>

<script>
    import vModal from '@/components/modal/v-modal';
    import {
        vIconButton,
        vIcon,
        vSelect,
        vErrorMessages,
    } from '@/components/form';
    import vPopper from '@/components/v-popper';
    import taxCategories from '@/data/tax-categories';
    import {required, minLength} from 'vuelidate/lib/validators';
    import vCustomFieldsManager from '@/components/custom-fields/v-custom-fields-manager';
    import ProductModel from '@/common/models/product-model';

    export default {
        name: 'product-form-modal',
        components: {
            vModal,
            vIcon,
            vIconButton,
            vSelect,
            vPopper,
            vErrorMessages,
            vCustomFieldsManager,
        },
        props: {
            value: {
                type: Object,
            },
            planIsLoading: {
                type: Boolean,
                default: false,
            },
            productsCustomFieldsSchema: Array,
            plans: {
                type: Array,
                default: () => [],
            },
        },
        data() {
            return {
                showProductUpdateModal: false,
                showProductDeleteModal: false,
                product: new ProductModel({...this.value}),
                taxCategoriesOptions: taxCategories,
            };
        },
        validations: {
            product: {
                name: {
                    required,
                    minLength: minLength(3),
                },
            },
        },
        computed: {
            isTaxable: {
                get() {
                    return this.product.taxCategoryId !== null;
                },
                set(value) {
                    this.product.taxCategoryId = !value ? null : '00000';
                },
            },
            hasPlans() {
                return this.plans.length;
            },
        },
        methods: {
            toggleModal(type) {
                const flag = `showProduct${type}Modal`;
                this[flag] = !this[flag];
            },
            nameWithTaxCode({name, taxCode}) {
                return `${name} (${taxCode})`;
            },
            closeProductForm() {
                this.showProductUpdateModal = false;
            },
            updateProduct() {
                this.$v.$touch();
                if (!this.$v.product.$error) {
                    this.$emit('input', this.product);
                    this.closeProductForm();
                }
            },
            deleteProduct() {
                this.$emit('delete', this.product);
                this.toggleModal('Delete');
            },
        },
    };
</script>
